import { Component, type ReactElement } from "react";
import { connect } from "react-redux";

import {
  ActionButton,
  ActionButtonIconPlacement,
  ActionButtonIntent,
  ActionButtonSize,
  Center,
  Collapse,
  TimeAgo,
} from "widgets";

import { ProjectColoredLink } from "@/modules/projects/components/ProjectColoredWidgets.js";
import type { Project, ProjectActivity as ProjectActivityGql, User } from "../../../graphql.js";
import { getProjectLabel } from "../../../redux/modules/config.js";
import { TooltipController } from "../../core/overlays/components/OverlayControllers.js";
import { ProjectActivity } from "../../projects/ProjectActivity.js";

interface IRelatedProjectComponentOwnProps {
  project: Project;
  activities: ProjectActivityGql[];
  currentUser: User;
  collapsed?: boolean;
}

type IRelatedProjectComponentDispatchProps = {};

export interface IRelatedProjectComponentProps
  extends IRelatedProjectComponentOwnProps,
    IRelatedProjectComponentDispatchProps {}

interface IRelatedProjectCompoundState {
  collapsed?: boolean;
}

function mapDispatchToProps(_dispatch): IRelatedProjectComponentDispatchProps {
  return {};
}

const INITIALLY_SHOWN = 1;

// defines how many activities are always shown

/**
 * A panel for a single project related to the main object of the RelatedProjectCard.
 * The list of activities for this project is collapsible after the first N activities.
 *
 * created on 11/11/16
 */
class RelatedProjectPanel extends Component<IRelatedProjectComponentProps, IRelatedProjectCompoundState> {
  constructor(props: IRelatedProjectComponentProps) {
    super(props);
    this.togglePanel = this.togglePanel.bind(this);
    this.buildActivities = this.buildActivities.bind(this);
    this.buildCollapsibleActivities = this.buildCollapsibleActivities.bind(this);
    this.buildToggle = this.buildToggle.bind(this);
    this.state = {
      collapsed: !!props.collapsed,
    };
  }

  togglePanel() {
    this.setState({ collapsed: !this.state.collapsed });
  }

  buildActivities(activities: ProjectActivityGql[]): ReactElement[] {
    const projectId: number = this.props.project.id;
    const currentUser: User = this.props.currentUser;

    return activities.map((activity) => {
      return (
        <ProjectActivity
          key={`rel-prj-${projectId}-act-${activity.id}`}
          activity={activity}
          currentUser={currentUser}
          users={this.props.project.projectMembers ? this.props.project.projectMembers.map((x) => x.user) : []}
        />
      );
    });
  }

  buildCollapsibleActivities(activities: ProjectActivityGql[]): ReactElement {
    return (
      <Collapse isExpanded={!this.state.collapsed}>
        <ul className="related-activities-list">{this.buildActivities(activities)}</ul>
      </Collapse>
    );
  }

  buildToggle(): ReactElement {
    return (
      <Center>
        <ActionButton
          icon={this.state.collapsed ? "keyboard_double_arrow_down" : "keyboard_double_arrow_up"}
          label={this.state.collapsed ? `Show All ${this.props.activities.length} Activities` : "Show Fewer Activities"}
          onPress={this.togglePanel}
          tooltip={`View all activities related to this genome in ${this.props.project.name}`}
          size={ActionButtonSize.Small}
          iconPlacement={ActionButtonIconPlacement.Right}
          intent={ActionButtonIntent.Outlined}
          style={{ marginTop: 10 }}
        />
      </Center>
    );
  }

  render(): ReactElement {
    const props = this.props;
    const project: Project = props.project;

    let visibleActivities: ReactElement[] = null;
    let toggleDisplay: ReactElement = null;
    let collapsibleActivities: ReactElement = null;

    if (props.activities) {
      visibleActivities = this.buildActivities(props.activities.slice(0, INITIALLY_SHOWN));

      if (props.activities.length > INITIALLY_SHOWN) {
        collapsibleActivities = this.buildCollapsibleActivities(props.activities.slice(INITIALLY_SHOWN));
        toggleDisplay = this.buildToggle();
      }
    }

    return (
      <div>
        <TooltipController content={`Go to this ${getProjectLabel(false, false)}`}>
          {(tooltipTriggerProps) => (
            <p {...tooltipTriggerProps}>
              <span className="highlight-title">
                <ProjectColoredLink project={project} />
              </span>
              <br />
              {project.lastModified ? (
                <span className="byline">
                  last activity <TimeAgo dateTimeISO={project.lastModified} />
                </span>
              ) : null}
            </p>
          )}
        </TooltipController>
        <p>{project.description}</p>
        <ul className="related-activities-list">{visibleActivities}</ul>
        {collapsibleActivities}
        {toggleDisplay}
        <hr />
      </div>
    );
  }
}

export { RelatedProjectPanel };

export default connect<
  IRelatedProjectComponentProps,
  IRelatedProjectComponentDispatchProps,
  IRelatedProjectComponentOwnProps
>(
  undefined,
  mapDispatchToProps,
)(RelatedProjectPanel);
